<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { generateStatisticsFileName } from 'dashboard/helper/downloadHelper';
import ReportFiltersSection from './ReportFiltersSection.vue';
import SummaryMetricCards from './SummaryMetricCards.vue';
import SummaryReportCharts from './SummaryReportCharts.vue';
import SummaryOverviewHeatmaps from './SummaryOverviewHeatmaps.vue';
import SummaryOverviewTeamsTable from './SummaryOverviewTeamsTable.vue';
import SummaryOverviewTagsTable from './SummaryOverviewTagsTable.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import { parseSummaryEntityFilterParams } from '../helpers/reportFilterHelper';

const store = useStore();
const route = useRoute();
const { t } = useI18n();

const from = ref(0);
const to = ref(0);
const businessHours = ref(false);
const selectedLabelIds = ref([]);
const excludeLabelIds = ref([]);
const selectedInboxIds = ref([]);
const selectedTeamIds = ref([]);
const selectedAgentIds = ref([]);
const resolutionMethod = ref('all');
const comparisonPeriods = ref([]);

const applyEntityFilters = filters => {
  selectedAgentIds.value = filters.agentIds || [];
  selectedInboxIds.value = filters.inboxIds || [];
  selectedTeamIds.value = filters.teamIds || [];
  selectedLabelIds.value = filters.labelIds || [];
  excludeLabelIds.value = filters.excludeLabelIds || [];
  resolutionMethod.value = filters.resolutionMethod || 'all';
};

applyEntityFilters(parseSummaryEntityFilterParams(route.query));

const allLabels = useMapGetter('labels/getLabels');

const chartFilters = computed(() => ({
  from: from.value,
  to: to.value,
  businessHours: businessHours.value,
  inboxIds: selectedInboxIds.value,
  teamIds: selectedTeamIds.value,
  labelIds: selectedLabelIds.value,
  excludeLabelIds: excludeLabelIds.value,
  agentIds: selectedAgentIds.value,
  resolutionMethod: resolutionMethod.value,
  comparisonPeriods: comparisonPeriods.value,
}));

const archiveContext = computed(() => {
  const labelTitles = selectedLabelIds.value
    .map(id => allLabels.value?.find(label => label.id === id)?.title)
    .filter(Boolean);
  const excludeLabelTitles = excludeLabelIds.value
    .map(id => allLabels.value?.find(label => label.id === id)?.title)
    .filter(Boolean);

  return {
    reportType: 'account',
    from: from.value,
    to: to.value,
    entityFilters: {
      agentIds: selectedAgentIds.value,
      inboxIds: selectedInboxIds.value,
      teamIds: selectedTeamIds.value,
      labelIds: selectedLabelIds.value,
      labelTitles,
      excludeLabelIds: excludeLabelIds.value,
      excludeLabelTitles,
      resolutionMethod: resolutionMethod.value,
    },
  };
});

const buildSummaryParams = () => {
  const params = {
    since: from.value,
    until: to.value,
    businessHours: businessHours.value,
  };
  if (selectedLabelIds.value.length > 0) {
    params.labelIds = selectedLabelIds.value;
  }
  if (excludeLabelIds.value.length > 0) {
    params.excludeLabelIds = excludeLabelIds.value;
  }
  if (selectedInboxIds.value.length > 0) {
    params.inboxIds = selectedInboxIds.value;
  }
  if (selectedTeamIds.value.length > 0) {
    params.teamIds = selectedTeamIds.value;
  }
  if (selectedAgentIds.value.length > 0) {
    params.agentIds = selectedAgentIds.value;
  }
  if (resolutionMethod.value !== 'all') {
    params.resolutionMethod = resolutionMethod.value;
  }
  if (comparisonPeriods.value.length) {
    params.comparisonPeriods = comparisonPeriods.value;
  }
  return params;
};

const uiFlags = useMapGetter('summaryReports/getUIFlags');
const isLoading = computed(
  () => uiFlags.value.isFetchingAccountOverviewReports
);

const overviewReport = useMapGetter('summaryReports/getAccountOverviewReport');
const overviewComparisons = useMapGetter(
  'summaryReports/getAccountOverviewComparisons'
);
const teamMetrics = useMapGetter('summaryReports/getTeamSummaryReports');
const teamComparisons = useMapGetter(
  'summaryReports/getTeamSummaryComparisons'
);
const labelMetrics = useMapGetter('summaryReports/getLabelSummaryReports');
const labelAggregates = useMapGetter(
  'summaryReports/getLabelSummaryAggregates'
);
const teams = useMapGetter('teams/getTeams');

const metricCardData = computed(() => {
  const current = overviewReport.value || {};
  return [current];
});

const comparisonSlices = computed(() =>
  (overviewComparisons.value || []).map(slice => ({
    ...slice,
    data: Array.isArray(slice.data) ? slice.data : [slice.data || {}],
  }))
);

let lastFetchKey = '';

const fetchReportsWithRetry = async () => {
  const params = buildSummaryParams();
  if (!params.since || !params.until) return;

  const fetchKey = JSON.stringify(params);
  if (fetchKey === lastFetchKey) return;
  lastFetchKey = fetchKey;

  const load = () =>
    Promise.all([
      store.dispatch('summaryReports/fetchAccountOverviewReports', params),
      store.dispatch('summaryReports/fetchTeamSummaryReports', params),
      store.dispatch('summaryReports/fetchLabelSummaryReports', params),
    ]);

  try {
    await load();
  } catch {
    try {
      await load();
    } catch {
      useAlert(t('REPORT.SUMMARY_FETCHING_FAILED'));
      lastFetchKey = '';
    }
  }
};

const fetchAllData = () => {
  store.dispatch('teams/get');
  store.dispatch('labels/get');
  fetchReportsWithRetry();
};

const onFiltersApply = ({ date, entity }) => {
  from.value = date.from;
  to.value = date.to;
  businessHours.value = date.businessHours;
  comparisonPeriods.value = date.comparisonPeriods || [];
  applyEntityFilters(entity);
  fetchAllData();
};

const downloadReports = () => {
  const fileName = generateStatisticsFileName({
    section: 'overview',
    from: from.value,
    to: to.value,
  });
  store.dispatch('downloadOverviewReports', {
    from: from.value,
    to: to.value,
    fileName,
    businessHours: businessHours.value,
    labelIds: selectedLabelIds.value,
    excludeLabelIds: excludeLabelIds.value,
    inboxIds: selectedInboxIds.value,
    teamIds: selectedTeamIds.value,
    agentIds: selectedAgentIds.value,
    resolutionMethod:
      resolutionMethod.value !== 'all' ? resolutionMethod.value : undefined,
    comparisonPeriods: comparisonPeriods.value,
  });
};

defineExpose({ downloadReports });
</script>

<template>
  <ReportFiltersSection
    type="account"
    :disabled="isLoading"
    @filters-apply="onFiltersApply"
  />
  <SummaryMetricCards
    type="account"
    :metrics="metricCardData"
    :archive-context="archiveContext"
    :primary-from="from"
    :primary-to="to"
    :comparison-slices="comparisonSlices"
  />
  <SummaryReportCharts
    type="account"
    :filters="chartFilters"
    :archive-context="archiveContext"
  />
  <SummaryOverviewHeatmaps
    :filters="chartFilters"
    :archive-context="archiveContext"
  />
  <SummaryOverviewTeamsTable
    :teams="teams"
    :team-metrics="teamMetrics"
    :team-comparisons="teamComparisons"
    :archive-context="archiveContext"
  />
  <SummaryOverviewTagsTable
    :label-metrics="labelMetrics"
    :without-tags-count="labelAggregates.withoutTagsCount || 0"
    :without-tags-report="labelAggregates.withoutTagsReport"
    :archive-context="archiveContext"
  />

  <div
    v-if="isLoading"
    class="fixed inset-0 z-40 flex items-start justify-center pt-[12.5rem] bg-n-solid-1/40 pointer-events-none"
  >
    <Spinner :size="32" class="text-n-brand" />
  </div>
</template>
